import type { Workspace } from "./workspace"
import { parseWorkspace } from "./workspace"

export interface Account { email: string; name: string | null; isProvider: boolean }
export interface Me { configured: boolean; user: Account | null }
export interface RemoteMeta { id: string; label: string; updatedAt: string; createdAt: string }

export async function fetchMe(): Promise<Me> {
  try {
    const r = await fetch("/api/auth/me", { credentials: "same-origin", cache: "no-store" })
    if (!r.ok) return { configured: false, user: null }
    return (await r.json()) as Me
  } catch {
    return { configured: false, user: null }
  }
}

export async function cloudList(): Promise<RemoteMeta[] | null> {
  try {
    const r = await fetch("/api/workspaces", { credentials: "same-origin", cache: "no-store" })
    if (!r.ok) return null
    return ((await r.json()) as { workspaces: RemoteMeta[] }).workspaces
  } catch { return null }
}

export async function cloudGet(id: string): Promise<Workspace | null> {
  try {
    const r = await fetch(`/api/workspaces/${encodeURIComponent(id)}`, { credentials: "same-origin", cache: "no-store" })
    if (!r.ok) return null
    return parseWorkspace(((await r.json()) as { workspace: unknown }).workspace)
  } catch { return null }
}

export type PutResult = { ok: true } | { ok: false; conflict?: Workspace; reason: "network" | "conflict" | "limit" | "auth" | "error" }

export async function cloudPut(w: Workspace): Promise<PutResult> {
  try {
    const r = await fetch(`/api/workspaces/${encodeURIComponent(w.id)}`, {
      method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(w),
    })
    if (r.ok) return { ok: true }
    if (r.status === 409) return { ok: false, reason: "conflict", conflict: parseWorkspace(((await r.json()) as { workspace: unknown }).workspace) ?? undefined }
    if (r.status === 429) return { ok: false, reason: "limit" }
    if (r.status === 401) return { ok: false, reason: "auth" }
    return { ok: false, reason: "error" }
  } catch { return { ok: false, reason: "network" } }
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }).catch(() => undefined)
}

/** Deletes the account and every saved incident on our servers. Local copies in this browser are untouched. */
export async function deleteAccountAndData(): Promise<boolean> {
  try { return (await fetch("/api/workspaces", { method: "DELETE", credentials: "same-origin" })).ok } catch { return false }
}

/**
 * Decide what to copy where after sign-in (newest updatedAt wins, per incident).
 * Nothing is ever deleted by a sync; guest work merges into the account.
 */
export function planSync(
  local: { id: string; updatedAt: string }[],
  remote: { id: string; updatedAt: string }[],
): { upload: string[]; download: string[] } {
  const r = new Map(remote.map((x) => [x.id, x.updatedAt]))
  const l = new Map(local.map((x) => [x.id, x.updatedAt]))
  const upload = local.filter((x) => !r.has(x.id) || (r.get(x.id) as string) < x.updatedAt).map((x) => x.id)
  const download = remote.filter((x) => !l.has(x.id) || (l.get(x.id) as string) < x.updatedAt).map((x) => x.id)
  return { upload, download }
}

export function loginUrl(next: string): string {
  return `/api/auth/google/login?next=${encodeURIComponent(next)}`
}
